/**
 * Points Manager Service
 * 
 * Manages the points economy: earning, spending, and tracking points.
 * Points are awarded for level completions, streaks, and extra practice tiers.
 * 
 * Points are stored in both localStorage (for fast access) and Firestore (for persistence).
 * Uses UnifiedBatchSyncService for optimized Firestore writes.
 */

import type {
  PointTransaction,
  PointTransactionType,
  UserPoints
} from '../types/gamification';
import {
  EXTRA_PRACTICE_TIERS,
  POINTS_CONFIG,
  STREAK_BADGES,
  STREAK_BADGE_BONUSES,
} from '../types/gamification';
import { batchSyncService } from './UnifiedBatchSyncService';

const STORAGE_KEY_PREFIX = 'user_points_';

function emptyPoints(): UserPoints {
  return { total: 0, earned: 0, spent: 0, history: [] };
}

/**
 * In-session authority for each user's points.
 *
 * Every mutation is a read-modify-write of one document, but the read used to
 * prefer Firestore while the write only lands there after the 5s batch
 * debounce. A single level completion awards several times (level, streak,
 * ring tier, perfect accuracy, PB), so award #2 read the pre-award total and
 * wrote it back — silently discarding award #1, and often dropping the total
 * *below* what the user had just been shown. Awards therefore looked like they
 * were never added.
 *
 * The fix has two halves: mutations run one-at-a-time (`runExclusive`), and
 * every read resolves against the most advanced view we know of, which is this
 * map until the debounced flush catches up.
 */
const sessionPoints = new Map<string, UserPoints>();

/**
 * Forget the in-session state, so the next read falls back to the caches.
 * Used when an admin edit makes Firestore authoritative — otherwise a tab that
 * has been open since before the edit would out-rank it.
 */
export function resetPointsSessionState(userId: string): void {
  sessionPoints.delete(userId);
}

let mutationChain: Promise<unknown> = Promise.resolve();

function runExclusive<T>(fn: () => Promise<T>): Promise<T> {
  const result = mutationChain.then(fn, fn);
  mutationChain = result.catch(() => undefined);
  return result;
}

/**
 * `earned` and `spent` only ever grow, so the candidate with the greatest
 * (earned + spent) has seen every transaction the others have. A stale view
 * can never win, which is what makes clobbering impossible.
 */
function progressOf(points: UserPoints | null | undefined): number {
  if (!points) return -1;
  return (points.earned ?? 0) + (points.spent ?? 0);
}

function mostAdvanced(...candidates: Array<UserPoints | null | undefined>): UserPoints {
  let best: UserPoints | null = null;
  for (const candidate of candidates) {
    if (progressOf(candidate) > progressOf(best)) best = candidate!;
  }
  return best ?? emptyPoints();
}

function readLocalPoints(userId: string): UserPoints | null {
  try {
    const data = localStorage.getItem(`${STORAGE_KEY_PREFIX}${userId}`);
    return data ? (JSON.parse(data) as UserPoints) : null;
  } catch (error) {
    console.error('Error loading user points from cache:', error);
    return null;
  }
}

/**
 * Load user points - FIRESTORE FIRST, reconciled against unflushed local state
 */
export async function getPoints(userId: string): Promise<UserPoints> {
  let firestorePoints: UserPoints | null = null;

  try {
    const { firestoreService } = await import('./database/FirestoreService');
    firestorePoints = await firestoreService.getUserPoints(userId);
  } catch (error) {
    console.warn('⚠️ Failed to load points from Firestore, using cache:', error);
  }

  const resolved = mostAdvanced(sessionPoints.get(userId), readLocalPoints(userId), firestorePoints);
  sessionPoints.set(userId, resolved);

  if (firestorePoints && resolved === firestorePoints) {
    // Firestore is ahead (another tab/device): refresh the cache from it.
    try {
      localStorage.setItem(`${STORAGE_KEY_PREFIX}${userId}`, JSON.stringify(resolved));
    } catch {
      // Quota — non-critical, the Firestore doc is the durable copy.
    }
    console.log(`☁️ Loaded ${resolved.total} points from Firestore`);
  }

  return resolved;
}

/**
 * Synchronous version for backwards compatibility - local state only
 */
export function getPointsSync(userId: string): UserPoints {
  return mostAdvanced(sessionPoints.get(userId), readLocalPoints(userId));
}

/**
 * Save user points to localStorage and queue for batch sync
 */
async function savePoints(userId: string, points: UserPoints): Promise<void> {
  // Record before any I/O: this is what later reads in this session resolve
  // against, and it must be authoritative even if the cache write fails.
  sessionPoints.set(userId, points);

  try {
    // Save to localStorage immediately
    const key = `${STORAGE_KEY_PREFIX}${userId}`;
    try {
      localStorage.setItem(key, JSON.stringify(points));
      console.log(`💾 Saved ${points.total} points to localStorage`);
    } catch (cacheError) {
      if (cacheError instanceof Error && cacheError.name === 'QuotaExceededError') {
        // The batch sync reads its payload from getPointsSync(), so before the
        // in-session map existed a full localStorage meant every flush wrote
        // the last value that HAD fit — freezing the Firestore copy at that
        // point while awards kept succeeding in memory. sessionPoints is now
        // the flush source, so the award still reaches Firestore.
        console.warn(
          `⚠️ localStorage quota exceeded — points (${points.total}) not cached, syncing from memory`,
        );
      } else {
        throw cacheError;
      }
    }
    
    // Queue for unified batch sync (3-5 seconds)
    batchSyncService.queuePointsUpdate(userId);
    
  } catch (error) {
    console.error('Error saving points:', error);
  }
}

/**
 * Sync points FROM Firestore to localStorage
 * Called when user logs in on a new browser/domain
 * Merges Firestore data with local data, preferring higher values
 */
export async function syncPointsFromFirestore(userId: string): Promise<boolean> {
  try {
    const { firestoreService } = await import('./database/FirestoreService');
    const firestorePoints = await firestoreService.getUserPoints(userId);
    const localPoints = await getPoints(userId);
    
    console.log(`💰 Points sync: Local=${localPoints.total}, Firestore=${firestorePoints?.total || 0}`);
    
    if (!firestorePoints || firestorePoints.total === 0) {
      // No points in Firestore - if we have local points, push them up
      if (localPoints.total > 0) {
        console.log(`💰 Pushing ${localPoints.total} local points UP to Firestore...`);
        await firestoreService.saveUserPoints(userId, localPoints);
        return true;
      }
      console.log('💰 No points in either location');
      return false;
    }
    
    // If Firestore has more points, use Firestore data
    if (firestorePoints.total > localPoints.total) {
      const key = `${STORAGE_KEY_PREFIX}${userId}`;
      try {
        localStorage.setItem(key, JSON.stringify(firestorePoints));
        console.log(`💰 Synced ${firestorePoints.total} points from Firestore (was ${localPoints.total} locally)`);
      } catch (cacheError) {
        if (cacheError instanceof Error && cacheError.name === 'QuotaExceededError') {
          console.warn('⚠️ localStorage quota exceeded - points loaded from Firestore but not cached');
        } else {
          throw cacheError;
        }
      }
      
      // Dispatch event to update UI
      window.dispatchEvent(new CustomEvent('pointsUpdated', { 
        detail: { total: firestorePoints.total, synced: true } 
      }));
      
      return true;
    } else if (localPoints.total > firestorePoints.total) {
      // Local has more points - push to Firestore to sync up
      console.log(`💰 Local has more points (${localPoints.total}) than Firestore (${firestorePoints.total}), syncing UP...`);
      await firestoreService.saveUserPoints(userId, localPoints);
      return true;
    }
    
    console.log('💰 Points are in sync');
    return false;
  } catch (error) {
    console.error('Error syncing points from Firestore:', error);
    return false;
  }
}

/**
 * Check if user has local points data
 */
export function hasLocalPoints(userId: string): boolean {
  const key = `${STORAGE_KEY_PREFIX}${userId}`;
  return localStorage.getItem(key) !== null;
}

/**
 * Generate a unique transaction ID
 */
function generateTransactionId(): string {
  return `pt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/** Points for an award type. Returns null for an unknown type. */
function amountForAward(type: PointTransactionType, metadata?: any): number | null {
  switch (type) {
    case 'level':
      return POINTS_CONFIG.LEVEL_COMPLETION;
    case 'streak':
      return POINTS_CONFIG.STREAK_BONUS;
    case 'extra': {
      // Extra practice tiers
      const tier = metadata?.tier || 1;
      if (tier === 1) return POINTS_CONFIG.EXTRA_TIER_1;
      if (tier === 2) return POINTS_CONFIG.EXTRA_TIER_2;
      if (tier === 3) return POINTS_CONFIG.EXTRA_TIER_3;
      return 0;
    }
    case 'bonus':
      // Free-form bonus award — caller passes the exact amount in metadata.
      // Used for the tournament endless-practice "while waiting" feature where
      // points scale with how many words the user typed (≈ words / 10).
      return Math.max(0, Math.floor(metadata?.amount ?? 0));
    default:
      console.warn(`Unknown point type: ${type}`);
      return null;
  }
}

/**
 * Award points to a user
 * ARCHITECTURE: Firestore-first with localStorage cache
 *
 * Serialized against every other mutation: a level completion fires several
 * awards back to back, and they must each build on the previous one's total
 * rather than on a snapshot taken before it.
 */
export async function awardPoints(
  userId: string,
  type: PointTransactionType,
  metadata?: any
): Promise<number> {
  const amount = amountForAward(type, metadata);
  if (amount === null) return 0;

  return runExclusive(async () => {
    const points = await getPoints(userId);

    // Create transaction
    const transaction: PointTransaction = {
      id: generateTransactionId(),
      type,
      amount,
      date: new Date().toISOString(),
      metadata
    };

    // Update points
    points.total += amount;
    points.earned += amount;
    points.history.push(transaction);

    // Keep only last 1000 transactions to avoid storage bloat
    if (points.history.length > 1000) {
      points.history = points.history.slice(-1000);
    }

    await savePoints(userId, points);

    // Dispatch event to update UI
    window.dispatchEvent(new CustomEvent('pointsUpdated', {
      detail: { amount, type, total: points.total }
    }));

    console.log(`💰 Awarded ${amount} points for ${type} (total ${points.total})`, metadata);

    return amount;
  });
}

/**
 * Award points synchronously (uses cache-first, syncs in background)
 * Use when you need immediate UI feedback
 */
export function awardPointsSync(
  userId: string,
  type: PointTransactionType,
  metadata?: any
): number {
  const amount = amountForAward(type, metadata);
  if (amount === null) return 0;

  // Mutates the in-session state in place, so an async award already holding
  // that same object sees this increment too. Prefer awardPoints() where the
  // caller can await.
  const points = getPointsSync(userId);

  const transaction: PointTransaction = {
    id: generateTransactionId(),
    type,
    amount,
    date: new Date().toISOString(),
    metadata
  };
  
  points.total += amount;
  points.earned += amount;
  points.history.push(transaction);
  
  if (points.history.length > 1000) {
    points.history = points.history.slice(-1000);
  }
  
  // Save async (non-blocking)
  savePoints(userId, points).catch(err => 
    console.error('Failed to sync points:', err)
  );
  
  window.dispatchEvent(new CustomEvent('pointsUpdated', { 
    detail: { amount, type, total: points.total } 
  }));
  
  console.log(`💰 Awarded ${amount} points for ${type}`, metadata);
  
  return amount;
}

/**
 * Spend points (for future features like streak freeze, time travel, merchandise)
 * ARCHITECTURE: Firestore-first with localStorage cache
 */
export async function spendPoints(
  userId: string,
  amount: number,
  reason: string,
  metadata?: any
): Promise<boolean> {
  return runExclusive(async () => {
    const points = await getPoints(userId);

    if (points.total < amount) {
      console.warn(`Insufficient points: have ${points.total}, need ${amount}`);
      return false;
    }

    // Create transaction
    const transaction: PointTransaction = {
      id: generateTransactionId(),
      type: 'purchase',
      amount: -amount,
      date: new Date().toISOString(),
      metadata: {
        ...metadata,
        reason
      }
    };

    // Update points
    points.total -= amount;
    points.spent += amount;
    points.history.push(transaction);

    // Keep only last 1000 transactions
    if (points.history.length > 1000) {
      points.history = points.history.slice(-1000);
    }

    await savePoints(userId, points);

    // Flush the queued Firestore write NOW, so a purchase is durable before the
    // UI reports it and so other tabs/devices see the deduction immediately.
    await batchSyncService.flushImmediately(userId);

    // Mirror awardPoints so header/profile listeners refresh after a spend.
    window.dispatchEvent(new CustomEvent('pointsUpdated', {
      detail: { amount: -amount, type: 'spend', total: points.total }
    }));

    console.log(`💸 Spent ${amount} points on ${reason}`, metadata);

    return true;
  });
}

/**
 * Award the one-time bonus for first reaching a streak badge tier.
 * Idempotent: scans points history for a prior claim of the same badge
 * (metadata.reason === 'streak_badge', metadata.badge === badge.name) and
 * skips if already awarded.
 *
 * Call after a user's streak ticks up (i.e. when justAchievedGoal is true).
 */
export async function awardStreakBadgeBonusIfReached(
  userId: string,
  currentStreak: number,
): Promise<{ amount: number; badge: string } | null> {
  if (currentStreak <= 0) return null;

  const badge = STREAK_BADGES.find(b =>
    currentStreak >= b.minDays && (b.maxDays === undefined || currentStreak <= b.maxDays)
  );
  if (!badge) return null;

  const bonusAmount = STREAK_BADGE_BONUSES[badge.name];
  if (!bonusAmount) return null;

  const points = await getPoints(userId);
  const alreadyClaimed = points.history.some(t =>
    t.type === 'bonus' &&
    t.metadata?.reason === 'streak_badge' &&
    t.metadata?.badge === badge.name
  );
  if (alreadyClaimed) return null;

  await awardPoints(userId, 'bonus', {
    amount: bonusAmount,
    reason: 'streak_badge',
    badge: badge.name,
    daysAtClaim: currentStreak,
  });

  return { amount: bonusAmount, badge: badge.name };
}

export interface ExtraPracticeAward {
  tier: number;
  label: string;
  amount: number;
  levelsRequired: number;
}

/** Local YYYY-MM-DD for an ISO transaction timestamp. */
function localDateOf(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Award the extra-practice tier bonuses (ring 2 / 3 / 4) for today's level
 * count. `levelsToday` is the cross-mode daily total, so this must be called
 * from EVERY mode that records a completion — the ring counter advances in all
 * of them.
 *
 * Awards on `levelsToday >= levelsRequired` rather than exact equality, and is
 * idempotent per (tier, local day) via points history. Together those mean a
 * tier crossed in a mode that didn't award — or skipped because the count
 * jumped by more than one — is still granted on the next completion, without
 * ever paying twice.
 *
 * Thresholds come from the admin-configurable ring milestones, falling back to
 * the EXTRA_PRACTICE_TIERS defaults (8 / 16 / 32).
 */
export async function awardExtraPracticeTiersIfReached(
  userId: string,
  levelsToday: number,
): Promise<{ total: number; awarded: ExtraPracticeAward[] }> {
  const empty = { total: 0, awarded: [] as ExtraPracticeAward[] };
  if (levelsToday <= 0) return empty;

  let thresholds: number[];
  try {
    const { loadGameplaySettings } = await import('../utils/gameplaySettingsUtils');
    const { ring2, ring3, ring4 } = (await loadGameplaySettings(userId)).ringMilestones;
    thresholds = [ring2, ring3, ring4];
  } catch (error) {
    console.warn('Ring milestones unavailable, using tier defaults:', error);
    thresholds = EXTRA_PRACTICE_TIERS.map(t => t.levelsRequired);
  }

  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const points = await getPoints(userId);
  const claimedToday = new Set(
    points.history
      .filter(t => t.type === 'extra' && localDateOf(t.date) === today)
      .map(t => t.metadata?.tier),
  );

  const awarded: ExtraPracticeAward[] = [];
  let total = 0;

  for (const [index, tierDef] of EXTRA_PRACTICE_TIERS.entries()) {
    const levelsRequired = thresholds[index] ?? tierDef.levelsRequired;
    if (levelsToday < levelsRequired) continue;
    if (claimedToday.has(tierDef.tier)) continue;

    const amount = await awardPoints(userId, 'extra', {
      tier: tierDef.tier,
      levelsCompleted: levelsToday,
      levelsRequired,
    });
    if (amount > 0) {
      awarded.push({
        tier: tierDef.tier,
        label: tierDef.label,
        amount,
        levelsRequired,
      });
      total += amount;
    }
  }

  return { total, awarded };
}

/**
 * Award the +5 personal-best WPM bonus when a user sets a new top WPM on a
 * words-only level (contentType === 'text'). Char-practice levels are excluded.
 *
 * Returns 0 if not eligible, otherwise the bonus amount along with the previous
 * best WPM (for caller toast / UI).
 */
export async function awardPbWpmBonusIfEligible(
  userId: string,
  currentWpm: number,
  currentCompletionId: string,
  contentType: string,
): Promise<{ amount: number; previousBest: number } | null> {
  if (contentType !== 'text' || currentWpm <= 0) return null;

  const { levelCompletionService } = await import('./LevelCompletionService');
  const allCompletions = await levelCompletionService.getAllCompletions(userId);

  let previousBest = 0;
  for (const c of allCompletions) {
    if (c.contentType !== 'text') continue;
    if (c.id === currentCompletionId) continue;
    if (c.wpm > previousBest) previousBest = c.wpm;
  }

  if (currentWpm <= previousBest) return null;

  await awardPoints(userId, 'bonus', {
    amount: 5,
    reason: 'pb_wpm',
    wpm: currentWpm,
    previousBest,
    completionId: currentCompletionId,
  });

  return { amount: 5, previousBest };
}

/**
 * Award the +2 perfect-accuracy bonus the first time a user clears a level
 * at 100% accuracy. Idempotent per (user, levelId) via points history.
 */
export async function awardPerfectAccuracyBonusIfEligible(
  userId: string,
  accuracy: number,
  levelId: string,
  metadata?: Record<string, any>,
): Promise<number> {
  if (accuracy < 100 || !levelId) return 0;

  const points = await getPoints(userId);
  const alreadyClaimed = points.history.some(t =>
    t.type === 'bonus' &&
    t.metadata?.reason === 'perfect_accuracy' &&
    t.metadata?.levelId === levelId
  );
  if (alreadyClaimed) return 0;

  await awardPoints(userId, 'bonus', {
    amount: 2,
    reason: 'perfect_accuracy',
    levelId,
    ...metadata,
  });
  return 2;
}

/**
 * Get points history for a user
 */
export async function getPointsHistory(
  userId: string,
  limit?: number
): Promise<PointTransaction[]> {
  const points = await getPoints(userId);
  const history = [...points.history].reverse(); // Most recent first
  
  if (limit) {
    return history.slice(0, limit);
  }
  
  return history;
}

/**
 * Get total points earned today
 */
export async function getPointsEarnedToday(userId: string): Promise<number> {
  const points = await getPoints(userId);
  // Use local timezone to match user's perception of "today"
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  
  return points.history
    .filter(t => t.date.startsWith(today) && t.amount > 0)
    .reduce((sum, t) => sum + t.amount, 0);
}

